import type { AdminRole } from '@/api/types';

/**
 * What each role may do, mirrored from social-admin's route table (README, "Roles").
 *
 * Used only to decide what to show: a control the API would refuse is not offered, and the
 * navigation does not link to a screen the role cannot open. The API stays the authority.
 */
const ALL: readonly AdminRole[] = ['SUPER_ADMIN', 'OPERATIONS', 'BILLING', 'READ_ONLY'];
const SUPER_OPS: readonly AdminRole[] = ['SUPER_ADMIN', 'OPERATIONS'];
const SUPER_BILLING: readonly AdminRole[] = ['SUPER_ADMIN', 'BILLING'];
const SUPER_ONLY: readonly AdminRole[] = ['SUPER_ADMIN'];

export const PERMISSIONS = {
  'dashboard.view': ALL,
  'account.changePassword': ALL,

  'partners.view': ALL,
  'partners.manage': SUPER_OPS,
  'partners.operatingMode': SUPER_ONLY,
  'partnerKeys.manage': SUPER_OPS,

  'orgs.view': ALL,
  'orgs.changeStatus': SUPER_OPS,
  'orgs.transfer': SUPER_ONLY,
  'orgKeys.manage': SUPER_OPS,
  'directOrgs.create': SUPER_OPS,
  'directOrgs.changePlan': ['SUPER_ADMIN', 'OPERATIONS', 'BILLING'],

  'plans.view': ALL,
  'plans.manage': SUPER_BILLING,

  'usage.view': ALL,
  'usage.export': SUPER_BILLING,

  'maintenance.view': ALL,
  'maintenance.operate': SUPER_OPS,
  'vault.rotate': SUPER_ONLY,

  'audit.view': SUPER_OPS,
  'adminUsers.manage': SUPER_ONLY,
} as const satisfies Record<string, readonly AdminRole[]>;

export type Permission = keyof typeof PERMISSIONS;

export function can(role: AdminRole | null | undefined, permission: Permission): boolean {
  if (!role) return false;
  const allowed: readonly AdminRole[] = PERMISSIONS[permission];
  return allowed.includes(role);
}
